import React, { useState, useRef, useEffect, useMemo } from 'react';
import styled from 'astroturf/react';
import { GridColumn } from 'js/design_system/grid/DeprecatedGrid';
import { currentUser } from 'js/utils/user';
import ActionNav from './ActionNav';
import { FeedContext } from './contexts/feed';
import Feed from './Feed/Feed';
import useFeedConfig from './Feed/hooks/useFeedConfig';
import useNews from './Feed/hooks/useNews';
import useUsers from './Feed/hooks/useUsers';
import SuggestedAcademics from './SuggestedAcademics';
import CoauthorConfirmations from './CoauthorConfirmations';
import { ContinueReading } from './ContinueReading';
import { AffiliationPrompt } from './AffiliationPrompt';
import { RecommendedPodcasts } from './RecommendedPodcasts';
import { PostPrompt } from './PostPrompt';
import { StickySidebar as StickySidebarBase } from 'js/social/shared/StickySidebar';
import { getSocialNewsFeedPage } from 'js/social/api/feed_api';
import { getDiscoverAcademics } from 'js/social/api/suggested_api';
import PullToRefresh from 'js/social/shared/PullToRefresh';
import ErrorStateFeed from './ErrorStateFeed';
import AttachmentAutoDownloader from './shared/AttachmentAutoDownloader';
import { ReactQueryProvider } from './state/ReactQueryProvider';
import { SuggestionsProvider } from './state/SuggestionsProvider';
import { BigCardDiscover } from './shared/big_cards/BigCardDiscover';
import {
  BigCardBulkFollow,
  BigCardBulkFollowProps,
} from './shared/big_cards/BigCardBulkFollow';
import BulkFollowListModal from './shared/big_cards/BulkFollowListModal';
import { Spinner } from './shared/CreateSocialItem/Spinner';
import { SnackbarProvider } from 'js/social/state/SnackbarProvider';
import { NewsType, Paper } from 'js/social/shared/types';
import { BigCardLoadingSpinner } from './shared/big_cards/BigCardLoadingSpinner';
import { MarketingCarousel } from './shared/MarketingCarousel';
import { PodcastLibraryCarouselAd } from './shared/big_cards/PodcastLibraryCarouselAd';
import { ToastProvider } from 'js/design_system/toasts/ToastProvider';
import KiiCollectionModal from 'js/modals/kii_collection';
import InterruptionChooser from 'js/liswp/interruption_chooser';
import { ReaderProvider } from './CoreX/Reader/ReaderProvider';
import { OnboardingTour } from './OnboardingTour';
import { JJHomePageSearch } from './JJHomePageSearch';
import {
  retrieveStoredContent,
  retrieveLegacyContent,
  clearStoredContent,
} from 'js/impact_hub_v2/utils/contentStorage';
import type { ContentType } from 'js/impact_hub_v2/utils/contentStorage';

const PAGE = 'home';

const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const NewsFeed = styled(Feed)`
  padding-bottom: 100px;
`;

const NewsFeedContainer = styled.div`
  @import 'stylesheets/design_system/_deprecated_grid.scss';
  min-height: 100%;

  @include medium-up {
    padding: 16px 16px 0 16px;
  }
`;

const SpinnerContainer = styled.div`
  padding-top: 24px;
  margin-bottom: 12px;
  text-align: center;
`;

const UpdatingText = styled.div`
  text-align: center;
  color: $ds-black;
  font-size: 16px;
`;

const ContentContainer = styled.div`
  @import 'stylesheets/design_system/_deprecated_grid.scss';
  @import 'stylesheets/design_system/_color.scss';

  @include grid-container;

  @include breakpoint-small {
    margin: 0;
  }
`;

const Container = styled.div`
  @import 'stylesheets/header.scss';

  min-height: calc(100vh - var(--nav-desktop-height));
  display: flex;
  flex-direction: column;

  ${ContentContainer} {
    flex-grow: 1;
  }

  @media (max-width: $ds-tablet) {
    min-height: calc(100vh - var(--nav-mobile-height));
    padding-bottom: var(--nav-mobile-bottom-height);
  }
`;

const StickySidebar = styled(StickySidebarBase)`
  > :first-child {
    padding-top: 12px;
  }

  > * {
    max-width: 392px;
  }
`;

interface Props {
  user?: {
    firstName: string;
    profilePictureUrl: string;
    followersCount: number;
    followeesCount: number;
  };
  suggestions?: {
    count: number;
    hasMore: boolean;
  };
  hoistedContentType?: NewsType;
  hoistedContentId?: string;
  autoDownload?: boolean;
  showBigCardDiscover?: boolean;
  showBigCardBulkFollow?: boolean;
  kiiCollectionData?: {
    enabled: boolean;
    headingText: string;
  };
  bigCardBulkFollow?: BigCardBulkFollowProps;
  showFeedbackModal?: boolean;
  suggestedAffiliation?: {
    department: {
      university: {
        id: number;
        url: string;
        name: string;
      };
    };
  };
  bigCardTopicText?: string;
  sutdDownloadSpinnerWorkTitle?: string;
  showInlineReader?: boolean;
  showInlineReaderRelatedWorks?: boolean;
  showRatings?: boolean;
  onboardingTour?: boolean;
  recommendedPodcasts?: Array<{
    id: number;
    workId: number;
    workTitle: string;
    workAuthors: string;
    workThumbnailUrl: string | null;
    source: string;
  }>;
  podcastLibraryCarouselEnabled?: boolean;
  podcastLibraryFeedAdsEnabled?: boolean;
  podcastLibrarySidebarEnabled?: boolean;
}

const Home: React.FunctionComponent<Props> = ({
  user: additionalUserInfo,
  suggestions,
  hoistedContentId,
  hoistedContentType,
  autoDownload,
  showBigCardDiscover,
  showBigCardBulkFollow,
  bigCardBulkFollow,
  showFeedbackModal,
  suggestedAffiliation,
  sutdDownloadSpinnerWorkTitle,
  kiiCollectionData,
  showInlineReader = false,
  showInlineReaderRelatedWorks = false,
  showRatings = false,
  onboardingTour = false,
  recommendedPodcasts,
  podcastLibraryCarouselEnabled = false,
  podcastLibraryFeedAdsEnabled = false,
  podcastLibrarySidebarEnabled = false,
}) => {
  const feedConfig = useFeedConfig(PAGE, showRatings);
  const userActions = useUsers();
  const newsActions = useNews({
    page: feedConfig.page,
    getPage: getSocialNewsFeedPage,
    setMoreUsers: userActions.setMoreUsers,
    setFeedConfig: feedConfig.setFeedConfig,
    hoistedContentId,
    hoistedContentType,
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDiscoverFollowees, setShowDiscoverFollowees] = useState(false);
  const [refreshingNewsFeed, setRefreshingNewsFeed] = useState(false);
  const [showBigCardBulkFollowModal, setShowBigCardBulkFollowModal] =
    useState(false);
  const [showKiiModal, setShowKiiModal] = useState(true);
  const [showOnboardingTour, setShowOnboardingTour] = useState(onboardingTour);
  const [analogyData, setAnalogyData] = useState<{
    initialContent: string;
    initialPaper: Paper;
  } | null>(null);
  const [comicData, setComicData] = useState<{
    workId: number;
    workPaper: Paper;
  } | null>(null);
  const feedRef = useRef<HTMLDivElement | null>(null);

  // Check for AI content data from sessionStorage when URL flag is present
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const contentType = urlParams.get('ai-content-type') as ContentType | null;

    // Support legacy parameters for backward compatibility
    const hasAnalogyFlag = urlParams.get('analogy') === '1';
    const hasComicFlag = urlParams.get('comic') === '1';
    const hasPostFlag = urlParams.get('post') === '1';

    // Determine which content type to handle
    let typeToHandle: ContentType | null = contentType;
    if (!typeToHandle) {
      if (hasAnalogyFlag) typeToHandle = 'analogy';
      else if (hasComicFlag) typeToHandle = 'comic';
      else if (hasPostFlag) typeToHandle = 'post';
    }

    if (typeToHandle) {
      // Try new storage first, then legacy
      let storedData = retrieveStoredContent();
      if (!storedData) {
        storedData = retrieveLegacyContent();
      }

      if (storedData && storedData.contentType === typeToHandle) {
        // Handle text content (analogy, post)
        if (
          storedData.contentType === 'analogy' ||
          storedData.contentType === 'post'
        ) {
          setAnalogyData({
            initialContent: storedData.initialContent,
            initialPaper: storedData.workPaper as unknown as Paper,
          });
          setShowCreateModal(true);
        }
        // Handle comic content
        else if (storedData.contentType === 'comic' && currentUser?.premium) {
          setComicData({
            workId: storedData.workId,
            workPaper: storedData.workPaper as unknown as Paper,
          });
          setShowCreateModal(true);
        }

        clearStoredContent();
      }
    }
  }, []);

  // Randomize carousel items order
  const shuffledCarouselItems = useMemo(() => {
    const items = [];

    if (showBigCardDiscover) {
      items.push(
        <BigCardDiscover
          key="discover"
          onCtaClick={() => setShowDiscoverFollowees(true)}
        />,
      );
    }

    if (showBigCardBulkFollow && bigCardBulkFollow) {
      items.push(
        <BigCardBulkFollow
          key="bulk-follow"
          {...bigCardBulkFollow}
          onComplete={() => {
            setRefreshingNewsFeed(true);
            window.location.reload();
          }}
          onMoreClick={() => setShowBigCardBulkFollowModal(true)}
        />,
      );
    }

    if (podcastLibraryCarouselEnabled) {
      items.push(<PodcastLibraryCarouselAd key="podcast-library" />);
    }

    return shuffleArray(items);
  }, [
    showBigCardDiscover,
    showBigCardBulkFollow,
    bigCardBulkFollow,
    podcastLibraryCarouselEnabled,
  ]);

  /* The newsfeed is only for logged-in users,
  if `currentUser` is null here we have bigger problems */
  if (currentUser == null) return null;

  const showSpinnerForDownload =
    sutdDownloadSpinnerWorkTitle && !newsActions.hasLoadedNews;
  if (showSpinnerForDownload) {
    showBigCardDiscover = false;
    showBigCardBulkFollow = false;
  }

  const hoistedNews = newsActions.news?.[0];
  let autoDownloadingWork: Paper | undefined;
  let autoBookmarkingSource: string | undefined;
  if (
    autoDownload &&
    (hoistedContentType === 'paper_update' || hoistedContentType === 'work') &&
    hoistedContentType === hoistedNews?.newsType &&
    hoistedContentId === hoistedNews?.newsId?.toString() &&
    hoistedNews?.work
  ) {
    autoDownloadingWork = hoistedNews.work;
    autoBookmarkingSource = 'email';
  }

  return (
    <>
      {autoDownloadingWork && (
        <AttachmentAutoDownloader
          work={autoDownloadingWork}
          sourceForBookmarking={autoBookmarkingSource}
          newsFeedLoadedRef={newsActions.newsFeedLoadedRef}
        />
      )}
      <PullToRefresh
        onRefresh={newsActions.refreshFeed}
        canRefresh={!newsActions.isLoading && newsActions.news.length > 0}
        feedRef={feedRef}
      />
      <ReactQueryProvider>
        <SnackbarProvider>
          <FeedContext.Provider
            value={{
              useReporting: true,
              recordViews: true,
              feedConfig,
              userActions,
              newsActions,
              showInlineReader,
            }}
          >
            <ReaderProvider
              /* ReaderProvider is only necessary to show CoreX in-line reader
              for CoreX-styled work cards, when inline-reader is enabled. */
              updateWorkBookmarkStatus={newsActions.updateWorkBookmarkStatus}
              showRelatedPapersSidebar={showInlineReaderRelatedWorks}
            >
              <SuggestionsProvider count={10} page={'home'}>
                <Container>
                  <ContentContainer ref={feedRef}>
                    <GridColumn sm={0} md={2} lg={2} xl={2}>
                      <StickySidebar>
                        <ActionNav
                          onPost={() => setShowCreateModal(true)}
                          user={{
                            ...currentUser,
                            photo: additionalUserInfo?.profilePictureUrl || '',
                          }}
                          followersCount={
                            additionalUserInfo?.followersCount || 0
                          }
                          followeesCount={
                            additionalUserInfo?.followeesCount || 0
                          }
                          suggestions={suggestions}
                        />
                      </StickySidebar>
                    </GridColumn>
                    <GridColumn sm={2} md={6} lg={7} xl={7}>
                      <NewsFeedContainer>
                        {refreshingNewsFeed ? (
                          <>
                            <SpinnerContainer>
                              <Spinner />
                            </SpinnerContainer>
                            <UpdatingText>
                              Updating to include the academics you
                              newly&nbsp;followed
                            </UpdatingText>
                          </>
                        ) : (
                          <>
                            {showSpinnerForDownload && (
                              <BigCardLoadingSpinner
                                downloadWorkTitle={sutdDownloadSpinnerWorkTitle}
                              />
                            )}
                            {(showBigCardDiscover ||
                              showBigCardBulkFollow ||
                              podcastLibraryCarouselEnabled) &&
                              !showSpinnerForDownload && (
                                <>
                                  <MarketingCarousel
                                    items={shuffledCarouselItems}
                                  />
                                  {showBigCardDiscover && (
                                    <BulkFollowListModal
                                      open={showDiscoverFollowees}
                                      asyncFollowSuggSource={
                                        getDiscoverAcademics
                                      }
                                      initialFollowSuggestions={[]}
                                      cardType={'discover'}
                                      onClose={(followingsAdded) => {
                                        setShowDiscoverFollowees(false);
                                        if (followingsAdded) {
                                          setRefreshingNewsFeed(true);
                                          window.location.reload();
                                        }
                                      }}
                                    />
                                  )}
                                  {showBigCardBulkFollow &&
                                    bigCardBulkFollow && (
                                      <BulkFollowListModal
                                        open={showBigCardBulkFollowModal}
                                        initialFollowSuggestions={bigCardBulkFollow.users.map(
                                          (user) => {
                                            return {
                                              id: user.id,
                                              displayName: user.displayName,
                                              photoUrl: user.photo,
                                              universityName:
                                                user.university?.displayName,
                                              departmentName:
                                                user.department?.displayName,
                                            };
                                          },
                                        )}
                                        cardType={'bulk_follow'}
                                        onClose={(followingsAdded) => {
                                          setShowBigCardBulkFollowModal(false);
                                          if (followingsAdded) {
                                            setRefreshingNewsFeed(true);
                                            window.location.reload();
                                          }
                                        }}
                                        topic={bigCardBulkFollow.topic}
                                      />
                                    )}
                                </>
                              )}
                            {!showSpinnerForDownload && (
                              <PostPrompt
                                open={showCreateModal || undefined}
                                modalOnly={
                                  showBigCardDiscover || showBigCardBulkFollow
                                }
                                fullName={currentUser.display_name || ''}
                                photoUrl={
                                  additionalUserInfo?.profilePictureUrl || ''
                                }
                                profileUrl={currentUser.url || ''}
                                onClose={() => {
                                  setShowCreateModal(false);
                                  setAnalogyData(null);
                                  setComicData(null);
                                }}
                                initialPaper={analogyData?.initialPaper}
                                initialContent={analogyData?.initialContent}
                                comicWorkId={comicData?.workId}
                              />
                            )}

                            {kiiCollectionData?.enabled && (
                              <ToastProvider>
                                <KiiCollectionModal
                                  open={showKiiModal}
                                  onClose={() => {
                                    setShowKiiModal(false);
                                  }}
                                  kiiSource={'spider_2024_q4_dist_sys_kii_v1'}
                                  headingText={kiiCollectionData?.headingText}
                                  subheadingText={
                                    "Sharing a paper you've published helps us understand your research interests."
                                  }
                                />
                              </ToastProvider>
                            )}

                            {/* Move search input below onboarding cards and above feed, only for admins */}
                            {feedConfig.showJJHomePage && (
                              <div style={{ margin: '24px 0' }}>
                                <JJHomePageSearch />
                              </div>
                            )}

                            {newsActions.hasError ? (
                              <ErrorStateFeed />
                            ) : (
                              <NewsFeed
                                {...newsActions}
                                {...userActions}
                                trackInteractions={true}
                                showFeedbackModal={showFeedbackModal}
                                recommendedPodcasts={recommendedPodcasts}
                                podcastLibraryAdsEnabled={
                                  podcastLibraryFeedAdsEnabled
                                }
                              />
                            )}
                          </>
                        )}
                      </NewsFeedContainer>
                    </GridColumn>
                    <GridColumn sm={0} md={0} lg={3} xl={3}>
                      <StickySidebar>
                        {suggestedAffiliation ? (
                          <AffiliationPrompt
                            affiliation={suggestedAffiliation}
                          />
                        ) : (
                          <></>
                        )}
                        {podcastLibrarySidebarEnabled &&
                          recommendedPodcasts && (
                            <RecommendedPodcasts
                              podcasts={recommendedPodcasts}
                            />
                          )}
                        <CoauthorConfirmations />
                        <SuggestedAcademics />
                        <ContinueReading />
                      </StickySidebar>
                    </GridColumn>
                  </ContentContainer>
                </Container>
              </SuggestionsProvider>
            </ReaderProvider>
          </FeedContext.Provider>
        </SnackbarProvider>
      </ReactQueryProvider>
      {showOnboardingTour && (
        <OnboardingTour onComplete={() => setShowOnboardingTour(false)} />
      )}
      {/* Paper claiming modal via InterruptionChooser */}
      <InterruptionChooser
        premium={currentUser?.premium ?? false}
        pageType="home"
        forceRender={null}
        showTranslationModal={false}
        considerUserSentimentPopup={false}
      />
    </>
  );
};

export default Home;
